/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  CostDocumentEdit,
  CostDocumentItem,
  CostDocumentItemCalculation,
} from "@/models/api/costDocuments/CostDocumentEdit";
import {
  correctCostDocument,
  createCostDocument,
  editCostDocument,
  getBookingsToImportFile,
  getCostDocumentTypes,
  getDataForCorrectionCostDocument,
  getDataForEditedCostDocument,
  getDescriptionFromBookingAttributes,
  getRelatedSaleDocuments,
  getValidateCostDocumentNumber,
  importFileFromBooking,
} from "@/requests/costDocument";
import { Guid } from "@/static/guid";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { DataOptions } from "vuetify/types";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import { FileTmp } from "@/models/api/Files";
import { postRemoveTemporaryFile, postUploadFileToPreview } from "@/requests/general";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { FORM_OF_PAYMENTS } from "@/static/formOfPayments";
import { FORM_OF_PAYMENT_TYPE } from "@/static/formOfPaymentType";
import {
  BookingForCostDocument,
  BookingItemForCostDocumentResponse,
} from "@/models/api/bookings/BookingForCostDocument";
import { SelectItem } from "@/models/api/SelectItem";
import routerInstance from "@/plugins/router";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";
import { COST_DOCUMENT_ROUTE_NAMES } from "@/routes/costDocuments";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { generateVirtual } from "@/requests/document";
import { COST_HINT_TYPE } from "@/static/costHintTypeEnum";

export interface CostDocumentsAddEditStateInterface {
  costDocumentId: string | null;
  costDocument: CostDocumentEdit | null;
  costDocumentBeforeEdit: CostDocumentEdit | null;
  sessionId: string | null;
  importFileFromBookingsFilter: null | Array<CriterionItemRequest>;
  importFileFromBookingsTab: null | CriterionItemRequest;
  importFileFromBookingsDataTableOptions: DataOptions;
  importFileFromBookingsItems: Array<BookingForCostDocument>;
  importFileFromBookingsCount: number;
  isLoadingImportFileFromBookings: boolean;
  isFileUploading: boolean;
  isSameDates: boolean;
  documentTypes: Array<SelectItem>;
  withVouchers: boolean;
  loadAll: boolean;
  isCorrection: boolean;
  type: "add" | "edit" | "correction";
}

export const CostDocumentsAddEditState: CostDocumentsAddEditStateInterface = {
  costDocumentId: null,
  costDocument: null,
  costDocumentBeforeEdit: null,
  sessionId: null,
  importFileFromBookingsFilter: null,
  importFileFromBookingsTab: null,
  importFileFromBookingsDataTableOptions: new DefaultDataOptions(),
  importFileFromBookingsItems: [],
  importFileFromBookingsCount: -1,
  isLoadingImportFileFromBookings: false,
  isFileUploading: false,
  isSameDates: false,
  documentTypes: [],
  isCorrection: false,
  type: "add",
  withVouchers: false,
  loadAll: false,
};

const CostDocumentsAddEditModule = defineModule({
  state: CostDocumentsAddEditState as CostDocumentsAddEditStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setSessionId(state, sessionId: string | null) {
      state.sessionId = sessionId;
    },
    setCostDocument(state, param: CostDocumentEdit | null) {
      state.costDocument = param;
    },
    setCostDocumentBeforeEdit(state, param: CostDocumentEdit | null) {
      state.costDocumentBeforeEdit = param;
    },
    setCostDocumentId(state, costDocumentId: string | null) {
      state.costDocumentId = costDocumentId;
    },
    setImportFileFromBookingsFilter(state, param: Array<CriterionItemRequest>) {
      state.importFileFromBookingsFilter = param;
    },
    setImportFileFromBookingsTab(state, param: CriterionItemRequest) {
      state.importFileFromBookingsTab = param;
    },
    setImportFileFromBookingsDataOptions(state, param: DataOptions) {
      state.importFileFromBookingsDataTableOptions = param;
    },
    addCostDocumentItem(state) {
      state.costDocument?.costDocumentItems.push(new CostDocumentItem());
    },
    setImportFileFromBookingsLoading(state, isLoading: boolean) {
      state.isLoadingImportFileFromBookings = isLoading;
    },
    setImportFileFromBookingResponse(state, response: BookingItemForCostDocumentResponse) {
      state.importFileFromBookingsItems = response.items;
      state.importFileFromBookingsCount = response.totalCount;
      state.isLoadingImportFileFromBookings = false;
    },
    setIsFileUploading(state, isLoading: boolean) {
      state.isFileUploading = isLoading;
    },
    addFiles(state, files: Array<FileTmp>) {
      const costDocument = state.costDocument as CostDocumentEdit;
      costDocument.updateTemporaryFilesList = costDocument.updateTemporaryFilesList.concat(files);
    },
    setIsSameDates(state) {
      state.isSameDates = !state.isSameDates;
    },
    setIsSameDatesOnChangeDate(state, param: string | undefined) {
      const costDocument = state.costDocument as CostDocumentEdit;
      if (param != undefined) {
        costDocument.issueDate = param;
        costDocument.saleDate = param;
        costDocument.receiptDate = param;
        costDocument.paymentDate = param;
      } else {
        costDocument.saleDate = costDocument.issueDate;
        costDocument.receiptDate = costDocument.issueDate;
        costDocument.paymentDate = costDocument.issueDate;
      }
    },
    setDatesOnChangeIssueDate(state, param: string | undefined) {
      if (param && state.costDocument && state.costDocument.receiptDate < param) state.costDocument.receiptDate = param;
    },
    setDatesOnChangeReciptDate(state, param: string | undefined) {
      if (param && state.costDocument && state.costDocument.issueDate > param) state.costDocument.issueDate = param;
    },
    setDocumentTypes(state, param: Array<SelectItem>) {
      state.documentTypes = param;
    },
    setWithVouchers(state, param: boolean) {
      state.withVouchers = param;
    },
    setLoadAll(state, param: boolean) {
      state.loadAll = param;
    },
    setIsCorrection(state, param: boolean) {
      state.isCorrection = param;
    },
  },
  actions: {
    async newCostDocument(context) {
      const { commit, rootState, rootDispatch, dispatch } = _moduleActionContext(context);
      commit.setSessionId(Guid.newGuid());
      const costDocumentEdit = new CostDocumentEdit();
      await rootDispatch.user.downloadOfficeSettings();
      await rootDispatch.administration.costHints.getCostHintsForEditor(COST_HINT_TYPE.ServiceVATMargin);
      costDocumentEdit.currency = rootState.user.officeSettings?.baseCurrency as string;
      // TODO - why commented?
      // const exchangeRate = await rootDispatch.exchangeRate.getLastExchangeRate({
      //   exchangeDate: dayjs().format(DEFAULT_DATE_FORMAT),
      //   currency: costDocumentEdit.currency
      // });
      if (rootState.user.officeSettings?.userSystemDate) {
        const systemDate = rootState.user.officeSettings.userSystemDate;
        costDocumentEdit.issueDate = systemDate;
        costDocumentEdit.saleDate = systemDate;
        costDocumentEdit.paymentDate = systemDate;
        costDocumentEdit.receiptDate = systemDate;
        costDocumentEdit.exchangeDate = systemDate;
      }

      await rootDispatch.user.downloadOfficeFormOfPayments(FORM_OF_PAYMENT_TYPE.CostDocument);
      if (costDocumentEdit.formOfPaymentId == 0 && rootState.user.formOfPayments) {
        const formOfPayment = rootState.user.formOfPayments.find(
          (x) => x.id == FORM_OF_PAYMENTS.CreditCard || x.id == FORM_OF_PAYMENTS.CreditCardSystem
        );
        if (formOfPayment) costDocumentEdit.formOfPaymentId = formOfPayment.id;
      }
      commit.setCostDocument(costDocumentEdit);
      commit.setIsCorrection(false);
      await dispatch.downloadDocumentTypes();
    },
    async editCostDocument(context, costDocumentId) {
      const { commit, state, rootDispatch, dispatch } = _moduleActionContext(context);
      commit.setSessionId(Guid.newGuid());
      await rootDispatch.administration.costHints.getCostHintsForEditor(COST_HINT_TYPE.ServiceVATMargin);
      await rootDispatch.user.downloadOfficeFormOfPayments(FORM_OF_PAYMENT_TYPE.CostDocument);

      const response = await getDataForEditedCostDocument(costDocumentId, state.sessionId as string).catch(() => null);

      if (response != null) {
        commit.setIsCorrection(false);
        commit.setCostDocumentBeforeEdit(_.cloneDeep(response));
        commit.setCostDocument(response);
        await dispatch.downloadDocumentTypes();
      } else {
        await routerInstance.push({ name: "Cost documents" });
      }
    },
    async createCorrectionCostDocument(context, costDocumentId) {
      const { commit, state, rootDispatch, dispatch } = _moduleActionContext(context);
      commit.setSessionId(Guid.newGuid());
      await rootDispatch.administration.costHints.getCostHintsForEditor(COST_HINT_TYPE.ServiceVATMargin);
      await rootDispatch.user.downloadOfficeFormOfPayments(FORM_OF_PAYMENT_TYPE.CostDocument);

      const response = await getDataForCorrectionCostDocument(costDocumentId, state.sessionId as string).catch(
        () => null
      );
      if (response != null) {
        commit.setIsCorrection(true);
        commit.setCostDocumentBeforeEdit(_.cloneDeep(response));
        commit.setCostDocument(response);
        await dispatch.downloadDocumentTypes();
      } else {
        await routerInstance.push({ name: "Cost documents" });
      }
    },
    async downloadDocumentTypes(context) {
      const { commit, state } = _moduleActionContext(context);
      const response = await getCostDocumentTypes();
      commit.setDocumentTypes(response);
      if (state.costDocument && state.costDocument.documentTypeId == 0)
        state.costDocument.documentTypeId = state.documentTypes[0].value;
    },
    async downloadBookingsToImport(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setImportFileFromBookingsLoading(true);
      const resp = await getBookingsToImportFile({
        count: state.importFileFromBookingsDataTableOptions.itemsPerPage,
        page: state.importFileFromBookingsDataTableOptions.page - 1,
        filterName: FILTER_NAMES.CostDocumentImportBookingFiles,
        criterions:
          state.importFileFromBookingsFilter == null || state.importFileFromBookingsTab == null
            ? _.cloneDeep(state.importFileFromBookingsFilter)
            : state.importFileFromBookingsFilter.concat(state.importFileFromBookingsTab),
      });
      commit.setImportFileFromBookingResponse(resp);
    },
    async createTmpFileFromBookingFile(context, bookingId: number) {
      const { commit, state } = _moduleActionContext(context);
      const files = await importFileFromBooking(
        bookingId,
        state.sessionId as string,
        state.withVouchers,
        state.loadAll
      );
      commit.addFiles(files);
      const costDocument = state.costDocument as CostDocumentEdit;
      const description = await getDescriptionFromBookingAttributes(bookingId);
      costDocument.description = description;
      commit.setCostDocument(costDocument);
    },
    async validateDocumentNumber(context) {
      const { state } = _moduleActionContext(context);
      if (
        state.costDocument !== null &&
        state.costDocument.contractor !== null &&
        state.costDocument.contractor.importedContractorId !== null
      )
        await getValidateCostDocumentNumber({
          documentNumber: state.costDocument.documentNumber,
          documentNumberBeforeEdit:
            state.costDocumentBeforeEdit === null ? null : state.costDocumentBeforeEdit.documentNumber,
          contractorGuid: state.costDocument.contractor.importedContractorId as string,
          contractorGuidBeforeEdit:
            state.costDocumentBeforeEdit === null || state.costDocumentBeforeEdit.contractor == null
              ? null
              : state.costDocumentBeforeEdit.contractor.importedContractorId,
        });
    },
    async calculateCostDocument(context) {
      const { commit, state } = _moduleActionContext(context);
      let sum = 0;
      const costDocument = state.costDocument as CostDocumentEdit;
      for (const costDocumentItem of costDocument.costDocumentItems) {
        // noinspection SuspiciousTypeOfGuard
        const grossInCurrencyNumber =
          typeof costDocumentItem.grossInCurrency === "string"
            ? parseFloat(costDocumentItem.grossInCurrency)
            : costDocumentItem.grossInCurrency;
        sum += grossInCurrencyNumber;
      }
      costDocument.amount = sum;
      commit.setCostDocument(costDocument);
    },
    async calculateCostDocumentItem(context, costDocumentItem: CostDocumentItem): Promise<CostDocumentItem> {
      const { dispatch } = _moduleActionContext(context);
      const calculation = new CostDocumentItemCalculation(costDocumentItem);
      calculation.grossInCurrency = Math.round(calculation.netPriceInCurrency * calculation.quantity * 100) / 100;

      costDocumentItem.applyCalculation(calculation);
      await dispatch.calculateCostDocument();
      return costDocumentItem;
    },
    async changedCurrency(context, issueDateChange: boolean) {
      const { rootDispatch, commit, state } = _moduleActionContext(context);
      const costDocument = state.costDocument as CostDocumentEdit;
      let exchange;
      if (costDocument.exchangeDate >= costDocument.issueDate || issueDateChange) {
        const date = new Date(costDocument.issueDate);
        date.setDate(date.getDate() - 1);
        exchange = await rootDispatch.exchangeRate.getLastExchangeRate({
          exchangeDate: date.toDateString(),
          currency: costDocument.currency as string,
          skipAlert: true,
        });
      } else {
        exchange = await rootDispatch.exchangeRate.getLastExchangeRate({
          exchangeDate: costDocument.exchangeDate,
          currency: costDocument.currency as string,
          skipAlert: true,
        });
      }

      costDocument.exchangeDate = exchange.exchangeDate;
      costDocument.exchangeRate = exchange.exchangeRateValue;
      commit.setCostDocument(costDocument);
    },
    async calculateCostDocumentItemByGross(context, costDocumentItem: CostDocumentItem): Promise<CostDocumentItem> {
      const { dispatch } = _moduleActionContext(context);
      const calculation = new CostDocumentItemCalculation(costDocumentItem);
      calculation.netPriceInCurrency = Math.round((calculation.grossInCurrency / calculation.quantity) * 100) / 100;
      costDocumentItem.applyCalculation(calculation);
      costDocumentItem = await dispatch.calculateCostDocumentItem(costDocumentItem);
      return costDocumentItem;
      //TODO przerobić na przeliczenie backendowe
    },
    async create(context) {
      const { dispatch, state, rootDispatch } = _moduleActionContext(context);
      const costDocument = state.costDocument as CostDocumentEdit;
      await rootDispatch.waitForCompleteAllRequests();
      costDocument.sessionId = state.sessionId as string;

      await createCostDocument(costDocument);
      if (routerInstance.currentRoute.name == COST_DOCUMENT_ROUTE_NAMES.EDIT_COST_DOCUMENT) {
        // TODO - what with corrections -> return to list
        routerInstance.push({ name: COST_DOCUMENT_ROUTE_NAMES.COST_DOCUMENTS_VM });
      } else {
        await dispatch.newCostDocument();
      }
    },
    async edit(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const costDocument = state.costDocument as CostDocumentEdit;
      await rootDispatch.waitForCompleteAllRequests();
      costDocument.sessionId = state.sessionId as string;

      const result = await editCostDocument(costDocument, false).catch(() => {
        routerInstance.push({
          name: COST_DOCUMENT_ROUTE_NAMES.COST_DOCUMENTS_VM,
        });
        return null;
      });

      await DoubleConfirm.Process(result, rootDispatch.popup.addConfirm, async () => {
        await editCostDocument(costDocument, true);
        rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("saved").toString()), "success"));
      });

      await routerInstance.push({ name: COST_DOCUMENT_ROUTE_NAMES.COST_DOCUMENTS_VM });
    },
    async correct(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      const costDocument = state.costDocument as CostDocumentEdit;
      await rootDispatch.waitForCompleteAllRequests();
      costDocument.sessionId = state.sessionId as string;

      const createdCostDocumentId = await correctCostDocument(costDocument);

      const confirmQ1 = ConfirmModel.confirmBoolFromContent(filterCapitalize(i18n.t("shouldCreateVirtualDocuments")));

      const confirmedQ1 = await rootDispatch.popup.addConfirm(confirmQ1);
      if (confirmedQ1) {
        const relatedDocuments = await getRelatedSaleDocuments(createdCostDocumentId);
        const confirmQ2 = new ConfirmModel(
          filterCapitalize(i18n.t("selectItemsToCreateVirtualDocuments")),
          filterCapitalize(i18n.t("information")),
          [
            {
              text: "Anuluj",
              color: "warning",
              value: false,
              outlined: true,
              insertSpace: true,
            },
            {
              text: "Utwórz",
              color: "warning",
              value: true,
            },
          ],
          "600",
          undefined,
          "cost-document-virtual-popup-table",
          relatedDocuments
        );
        const confirmedQ2 = (await rootDispatch.popup.addConfirm(confirmQ2)) as {
          selectedItems: string[];
          value: boolean;
        };
        if (confirmedQ2.value) {
          await generateVirtual(createdCostDocumentId, confirmedQ2.selectedItems);
        }
      }

      await routerInstance.push({ name: COST_DOCUMENT_ROUTE_NAMES.COST_DOCUMENTS_VM });
    },
    async removeFile(context, file: FileTmp) {
      const { commit, state } = _moduleActionContext(context);
      await postRemoveTemporaryFile(file.id);
      const costDocument = state.costDocument as CostDocumentEdit;
      costDocument.updateTemporaryFilesList.splice(costDocument.updateTemporaryFilesList.indexOf(file), 1);
      commit.setCostDocument(costDocument);
    },
    async removeAllFiles(context) {
      const { commit, state } = _moduleActionContext(context);
      const costDocument = state.costDocument as CostDocumentEdit;
      const fileIds = costDocument.updateTemporaryFilesList.map((v) => v.id);
      for (const id of fileIds) {
        // noinspection ES6MissingAwait
        postRemoveTemporaryFile(id);
      }
      costDocument.updateTemporaryFilesList = [];
      commit.setCostDocument(costDocument);
    },
    async addFile(context, files: Array<File>) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsFileUploading(true);
      const filesResponse = await postUploadFileToPreview(files, state.sessionId as string);
      commit.setIsFileUploading(false);
      commit.addFiles(filesResponse);
    },
    async setIsSameDatesOnClick(context) {
      const { commit } = _moduleActionContext(context);
      commit.setIsSameDates();
      commit.setIsSameDatesOnChangeDate();
    },
    async setDatesOnChangeIssueDate(context, param) {
      const { commit, state } = _moduleActionContext(context);
      if (state.isSameDates) {
        commit.setIsSameDatesOnChangeDate(param);
      } else {
        commit.setDatesOnChangeIssueDate(param);
      }
    },
    async setDatesOnChangeSaleDate(context, param) {
      const { commit, state } = _moduleActionContext(context);
      if (state.isSameDates) {
        commit.setIsSameDatesOnChangeDate(param);
      }
    },
    async setDatesOnChangeReciptDate(context, param) {
      const { commit, state } = _moduleActionContext(context);
      if (state.isSameDates) {
        commit.setIsSameDatesOnChangeDate(param);
      } else {
        commit.setDatesOnChangeReciptDate(param);
      }
    },
    async setDatesOnChangePaymentDate(context, param) {
      const { commit, state } = _moduleActionContext(context);
      if (state.isSameDates) {
        commit.setIsSameDatesOnChangeDate(param);
      }
    },
  },
});

export default CostDocumentsAddEditModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, CostDocumentsAddEditModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, CostDocumentsAddEditModule);
